import React, { useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { forgotPassword, resetPassword } from "../../services/api";

const Layout = ({ children }) => {
  const [forgotPasswordData, setForgotPasswordData] = useState({ email: "" });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  
  // New password reset states
  const [resetPasswordData, setResetPasswordData] = useState({ 
    password: "", 
    confirmPassword: "" 
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState("");

  const handleForgotPassword = async (e) => {
    e?.preventDefault?.();
    setForgotPasswordLoading(true);
    setForgotPasswordMessage("");
    setForgotPasswordError("");

    try {
      const userId = sessionStorage.getItem("user_id");
      const payload = userId
        ? { ...forgotPasswordData, user_id: userId }
        : { ...forgotPasswordData };
      const response = await forgotPassword(payload);
      if (response.data.status === "success") {
        setForgotPasswordMessage(response.data.message);
        setForgotPasswordData({ email: "" });
      } else {
        setForgotPasswordError(response.data.message || "An error occurred");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        setForgotPasswordError(error.response.data.message);
      } else {
        setForgotPasswordError(
          "An error occurred while sending the password reset link. Please try again later."
        );
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotPasswordInputChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({ ...prev, [name]: value }));
    if (forgotPasswordMessage || forgotPasswordError) {
      setForgotPasswordMessage("");
      setForgotPasswordError("");
    }
  };

  // Password reset handlers
  const handleResetPassword = async (e) => {
    console.log('handleResetPassword called!');
    e?.preventDefault?.();
    setResetPasswordLoading(true);
    setResetPasswordMessage("");
    setResetPasswordError("");

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        setResetPasswordError("Please login first to reset your password");
        return;
      }

      if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
        setResetPasswordError("Passwords do not match");
        return;
      }

      if (resetPasswordData.password.length < 8) {
        setResetPasswordError("Password must be at least 8 characters long");
        return;
      }

      const payload = { 
        password: resetPasswordData.password,
        password_confirmation: resetPasswordData.confirmPassword
      };
      
      console.log('Sending password reset payload:', payload);
      console.log('Token:', token);
      
      const response = await resetPassword(token, payload);
      if (response.data.status === "success") {
        setResetPasswordMessage(response.data.message || "Password updated successfully!");
        setResetPasswordData({ password: "", confirmPassword: "" });
      } else {
        setResetPasswordError(response.data.message || "An error occurred");
      }
    } catch (error) {
      console.error('Password reset error:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.data?.message) {
        setResetPasswordError(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Handle Laravel validation errors
        const firstError = Object.values(error.response.data.errors)[0][0];
        setResetPasswordError(firstError);
      } else {
        setResetPasswordError(
          "An error occurred while updating your password. Please try again later."
        );
      }
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleResetPasswordInputChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData((prev) => ({ ...prev, [name]: value }));
    if (resetPasswordMessage || resetPasswordError) {
      setResetPasswordMessage("");
      setResetPasswordError("");
    }
  };

  return (
    <>
      <Header />
      <main>
        {children}
        <div
            className="modal fade"
            id="changePasswordModal"
            tabIndex="-1"
            aria-labelledby="changePasswordModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5" id="changePasswordModalLabel">
                    Change Password
                  </h1>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <form>
                    <h4 className="h4 pb-2">Change Your Password</h4>
                    <p className="line-h-150">
                      Enter your new password below. Make sure it's secure and easy to remember.
                    </p>

                    {resetPasswordMessage && (
                      <div className="alert alert-success" role="alert">
                        <i className="fa fa-check-circle me-2"></i>
                        {resetPasswordMessage}
                      </div>
                    )}

                    {resetPasswordError && (
                      <div className="alert alert-danger" role="alert">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        {resetPasswordError}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-floating mt-4">
                          <input
                            type="password"
                            className={`form-control ${resetPasswordData.password && resetPasswordData.password.length < 8 ? 'is-invalid' : ''}`}
                            id="newPassword"
                            name="password"
                            placeholder=" "
                            value={resetPasswordData.password}
                            onChange={handleResetPasswordInputChange}
                            required
                            disabled={resetPasswordLoading}
                            minLength="8"
                          />
                          <label htmlFor="newPassword">New Password</label>
                          {resetPasswordData.password && resetPasswordData.password.length < 8 && (
                            <div className="invalid-feedback text-danger">
                              Password must be at least 8 characters long
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-floating mt-4">
                          <input
                            type="password"
                            className={`form-control ${resetPasswordData.confirmPassword && resetPasswordData.password !== resetPasswordData.confirmPassword ? 'is-invalid' : ''}`}
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder=" "
                            value={resetPasswordData.confirmPassword}
                            onChange={handleResetPasswordInputChange}
                            required
                            disabled={resetPasswordLoading}
                            minLength="8"
                          />
                          <label htmlFor="confirmPassword">Confirm New Password</label>
                          {resetPasswordData.confirmPassword && resetPasswordData.password !== resetPasswordData.confirmPassword && (
                            <div className="invalid-feedback text-danger">
                              Passwords do not match
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary-s"
                    onClick={handleResetPassword}
                    disabled={resetPasswordLoading || !resetPasswordData.password.trim() || !resetPasswordData.confirmPassword.trim() || resetPasswordData.password !== resetPasswordData.confirmPassword}
                  >
                    {resetPasswordLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-1"></i> Updating...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-lock me-1"></i> Update Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Layout;
